import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { EditableText, useToast } from "@hightouch/htui";
import {
  type AmpLibraryFileFragment,
  useAmpLibraryUpdateFileMutation,
} from "src/graphql";
import {
  CanvasRenameContext,
  type PendingName,
  renameInput,
  useCanvasRename,
} from "src/pages/projects/library/canvas/canvas-rename";

export function CanvasRenameProvider({ children }: { children: ReactNode }) {
  const [nodeId, setNodeId] = useState<string | null>(null);
  const [pendingNames, setPendingNames] = useState<
    ReadonlyMap<string, PendingName>
  >(() => new Map());
  const stop = useCallback(
    (stopped: string) =>
      setNodeId((current) => (current === stopped ? null : current)),
    [],
  );
  const setPendingName = useCallback(
    (fileId: string, pending: PendingName | null) =>
      setPendingNames((current) => {
        const next = new Map(current);
        if (pending) next.set(fileId, pending);
        else next.delete(fileId);
        return next;
      }),
    [],
  );
  const value = useMemo(
    () => ({ nodeId, start: setNodeId, stop, pendingNames, setPendingName }),
    [nodeId, stop, pendingNames, setPendingName],
  );
  return (
    <CanvasRenameContext.Provider value={value}>
      {children}
    </CanvasRenameContext.Provider>
  );
}

/** The card's name as a field: Enter or a click away saves, Escape cancels. */
export function CanvasRenameField({
  nodeId,
  file,
}: {
  nodeId: string;
  file: AmpLibraryFileFragment;
}) {
  const update = useAmpLibraryUpdateFileMutation();
  const { toast } = useToast();
  const { stop, setPendingName } = useCanvasRename();
  // A click away can commit through both the blur and the outside click.
  const hasSaved = useRef(false);
  // A card removed mid-rename must not come back as a field.
  useEffect(() => () => stop(nodeId), [stop, nodeId]);

  const save = async (value: string) => {
    const name = value.trim();
    const input = renameInput(file, name);
    if (!name || name === file.name || !input || hasSaved.current) return;
    hasSaved.current = true;
    setPendingName(file.id, { versionId: file.versionId, name });
    try {
      // The mutation's success refetches the canvas, which brings the new version.
      const { updateAmpFile: result } = await update.mutateAsync({ input });
      if (result.file) return;
      setPendingName(file.id, null);
      toast({
        id: "canvas-rename-file",
        variant: "warning",
        title: "This file changed while you renamed it. Try again.",
      });
    } catch (error) {
      setPendingName(file.id, null);
      toast({
        id: "canvas-rename-file",
        variant: "error",
        title: "Could not rename file",
        message: error instanceof Error ? error.message : "Try again.",
      });
    }
  };

  return (
    <EditableText
      isEditing
      onEditingChange={(isEditing) => {
        if (!isEditing) stop(nodeId);
      }}
      value={file.name ?? ""}
      placeholder="Untitled"
      size="sm"
      variant="minimal"
      width="100%"
      inputProps={{ maxLength: 255 }}
      onChange={(value) => void save(value)}
    />
  );
}
